import {
    type OrgEmailActivityAction,
    OrgEmailActivityDrawer,
} from '@client/components/_business/email/OrgEmailActivityDrawer';
import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { confirmDestructive } from '@client/components/_ui/feedback/modal';
import { buildInitialWhereFromFilters, FilterBar } from '@client/components/filters';
import { TableViewsBar, useTableViews } from '@client/components/table-views';
import { useUserFilterConfig } from '@client/components/users/UserFilters';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { usePermissions } from '@client/hooks/auth/usePermissions';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useUser } from '@client/hooks/business/user/useUser';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { type RouterInputs, trpc } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE, ORG_ROLES } from '@shared/constants';
import { hasPermission } from '@shared/permissions/roles';
import { FilterOperatorSchema } from '@shared/schemas/filter';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import clsx from 'clsx';
import { User, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';
import { USER_TABLE_DEFAULT_HIDDEN_COLUMNS, type UserRow, useUserTableColumns } from './columns';
import { USER_BUILT_IN_VIEWS } from './components/user-built-in-views';
import { InviteUser } from './InviteUser';

export const Route = createFileRoute('/console/$orgId/configuration/org-settings/users/')({
    staticData: {
        metadata: { title: 'Team management' },
        ui: {
            icon: User,
            title: 'Team management',
            subtitle: 'Manage your team members, assign roles and permissions, and invite new users',
            menuDescription: 'Manage team members and assign roles',
        },
    },
    validateSearch: zodValidator(
        z.object({
            'user-filters': z
                .array(
                    z.union([
                        z.object({
                            id: z.string(),
                            filterId: z.literal('handlingPartyIds'),
                            operator: FilterOperatorSchema,
                            value: z.union([z.array(z.coerce.bigint()), z.coerce.bigint()]),
                        }),
                        z.object({
                            id: z.string(),
                            filterId: z.string(),
                            operator: FilterOperatorSchema,
                            value: z.any(),
                        }),
                    ]),
                )
                .optional()
                .catch(undefined),
            view: z.string().optional().catch(undefined),
        }),
    ),
    component: ConfigurationUsers,
});

type QueryParams = RouterInputs['user']['listUsers'];

const USER_TABLE_COLUMN_PERSIST_KEY = 'users-table-columns-v2';

function ConfigurationUsers() {
    const orgId = useOrgId();
    const navigate = useNavigate();
    const searchParams = Route.useSearch();
    const [openInviteUserDialog, setOpenInviteUserDialog] = useState(false);
    const [orgEmailActivityAction, setOrgEmailActivityAction] = useState<OrgEmailActivityAction>(['none']);
    const user = useUser();
    const { can } = usePermissions();
    const canAdminOrg = can('org:admin');
    const { trpcClient } = useTrpc();

    const canInviteUsers = hasPermission(user.roles, 'user:create');

    const goToUser = (userId: bigint) =>
        navigate({
            to: '/console/$orgId/configuration/org-settings/users/details/$userId',
            params: { orgId, userId },
        });

    const { config: filterConfig } = useUserFilterConfig();

    const [queryParams, setQueryParams] = usePathState<QueryParams>('query-params', {
        orderBy: [{ firstName: 'asc' }],
        limit: DEFAULT_PAGE_SIZE,
        where: buildInitialWhereFromFilters(filterConfig, searchParams['user-filters']),
    });

    // Postpone the list query until saved views resolve which view loads first, so we fetch once
    // with the resolved where/sort/pageSize instead of unfiltered-then-refiltered (set by onResolved).
    const [viewsReady, setViewsReady] = useState(false);

    // System and support accounts are excluded by the procedure itself (user.listUsers).
    const { data, isFetching, refetch } = trpc.user.listUsers.useQuery(queryParams, {
        enabled: viewsReady,
    });

    const getUserDisplayName = (userRow: UserRow) => {
        if (userRow.firstName && userRow.lastName) {
            return `${userRow.firstName} ${userRow.lastName}`;
        }
        if (userRow.firstName) {
            return userRow.firstName;
        }
        if (userRow.lastName) {
            return userRow.lastName;
        }
        return userRow.email;
    };

    const handleRevokeAccess = (userRow: UserRow) => {
        confirmDestructive({
            title: 'Revoke access',
            content: `Are you sure you want to revoke access for ${getUserDisplayName(userRow)}? They will no longer be able to log in to this organisation.`,
            okText: 'Revoke access',
            onOk: async () => {
                try {
                    await trpcClient.user.updateUser.mutate({
                        userId: userRow.userId,
                        set: { disabled: true },
                    });
                    message.success('Access revoked successfully');
                    void refetch();
                } catch (e) {
                    errorMessage.show(e);
                }
            },
        });
    };

    const tableColumns = useUserTableColumns({
        onEmailStatusClick: (userId, email) => {
            setOrgEmailActivityAction(['view', { userId, userEmail: email }]);
        },
        onEditUser: (userId) => {
            void goToUser(userId);
        },
        onRevokeAccess: handleRevokeAccess,
    });

    const tableProps = usePrismaTable({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: !viewsReady || isFetching,
        columns: tableColumns,
        rowKey: 'userId',
        queryParams,
        onParamsChange: setQueryParams,
        pagination: {
            position: 'both',
            showSizeChanger: true,
        },
        columnVisibility: {
            enabled: true,
            persistKey: USER_TABLE_COLUMN_PERSIST_KEY,
            enableReordering: true,
            defaultHidden: USER_TABLE_DEFAULT_HIDDEN_COLUMNS,
        },
        refreshable: {
            enabled: true,
            onRefresh: () => refetch(),
        },
        emptyState: { noRecordsTitle: 'No team members yet' },
    });

    const views = useTableViews({
        entityType: 'USER',
        config: filterConfig,
        urlFilterKey: 'user-filters',
        builtInViews: USER_BUILT_IN_VIEWS,
        defaultBuiltInViewId: '__active__',
        initialFilters: searchParams['user-filters'],
        initialActiveViewId: searchParams.view,
        queryParams,
        setQueryParams,
        table: tableProps,
        columnPersistKey: USER_TABLE_COLUMN_PERSIST_KEY,
        defaultOrderBy: [{ firstName: 'asc' }] as QueryParams['orderBy'],
        defaultPageSize: DEFAULT_PAGE_SIZE,
        defaultHidden: USER_TABLE_DEFAULT_HIDDEN_COLUMNS,
        onResolved: () => setViewsReady(true),
    });

    return (
        <PageController route={Route}>
            <TableViewsBar controller={views.controller} />

            <TableToolbar
                filters={
                    <FilterBar
                        config={filterConfig}
                        filters={views.filterState.filters}
                        whereClause={views.filterState.whereClause}
                        onAddFilter={views.filterState.addFilter}
                        onUpdateFilter={views.filterState.updateFilter}
                        onRemoveFilter={views.filterState.removeFilter}
                        onClearFilters={views.filterState.clearFilters}
                    />
                }
                variant="enhanced"
                actions={
                    canInviteUsers && (
                        <Button type="primary" icon={<UserPlus />} onClick={() => setOpenInviteUserDialog(true)}>
                            Invite user
                        </Button>
                    )
                }
            />
            {canInviteUsers && (
                <InviteUser
                    open={openInviteUserDialog}
                    onClose={() => {
                        setOpenInviteUserDialog(false);
                        void refetch();
                    }}
                    onInviteSuccess={(userId) => {
                        setOpenInviteUserDialog(false);
                        void refetch();
                        void goToUser(userId);
                    }}
                />
            )}

            <PrismaTable
                {...tableProps}
                rowClassName={(row) => {
                    const targetUserIsOrgAdmin = row.roles?.includes(ORG_ROLES.ORG_ADMIN);
                    const canEditThisUser =
                        hasPermission(user.roles, 'super:admin') || canAdminOrg || !targetUserIsOrgAdmin;

                    return clsx(
                        'transition-colors',
                        row.disabled && 'opacity-60',
                        canEditThisUser ? 'hover:bg-muted' : 'cursor-not-allowed',
                    );
                }}
                onRow={(row) => {
                    const targetUserIsOrgAdmin = row.roles?.includes(ORG_ROLES.ORG_ADMIN);
                    const canEditThisUser =
                        hasPermission(user.roles, 'super:admin') || canAdminOrg || !targetUserIsOrgAdmin;

                    return {
                        onClick: (e) => {
                            // Don't trigger row click if clicking on action buttons
                            const target = e.target as HTMLElement;
                            if (target.closest('.ant-dropdown-trigger') || target.closest('.ant-typography-copy')) {
                                return;
                            }
                            if (canEditThisUser) {
                                void goToUser(row.userId);
                            }
                        },
                    };
                }}
            />

            {/* Email Activity Drawer */}
            <OrgEmailActivityDrawer
                action={orgEmailActivityAction}
                onClose={() => {
                    setOrgEmailActivityAction(['none']);
                }}
            />
        </PageController>
    );
}
